"use client";

import { useEffect, useRef, useState } from "react";
import { List, X } from "@phosphor-icons/react/dist/ssr";

import { Button } from "@/src/components/ui/Button";
import { Logo } from "@/src/components/ui/Logo";
import { NavItem } from "@/src/components/ui/NavItem";
import { NavDropdown, type NavDropdownCluster } from "@/src/components/ui/NavDropdown";
import { cn } from "@/src/lib/utils";
import type { MenuNode, MenuTree } from "@/src/lib/menus/menus";

import { MobileNavItem } from "./MobileNavItem";

// Splits a menu tree's root nodes into CTA items (rendered as pill buttons
// in the right-side slot) vs regular nav items.
function splitCta(nodes: MenuNode[]) {
  const cta: MenuNode[] = [];
  const nav: MenuNode[] = [];
  for (const n of nodes) {
    if (n.isCta) cta.push(n);
    else nav.push(n);
  }
  return { cta, nav };
}

// Buckets a mega-menu parent's children into their admin-assigned columns
// (1/2/3), defaulting ungrouped items into column 1.
function groupByColumn(children: MenuNode[]): NavDropdownCluster[][] {
  const columns: NavDropdownCluster[][] = [[], [], []];
  for (const child of children) {
    const column = columns[child.column === 2 ? 1 : child.column === 3 ? 2 : 0];
    const last = column[column.length - 1];
    if (last && last.heading === child.group) {
      last.items.push(child);
    } else {
      column.push({ heading: child.group, items: [child] });
    }
  }
  return columns;
}

// The mobile menu is a flat list with no cluster info of its own, so group its links the
// way the desktop mega menu does: look each link up (by href) in the desktop clusters and
// follow their order. A group set explicitly on a mobile item in the admin wins; links the
// desktop menu doesn't know about trail at the end in their own order.
function clusterMobileChildren(children: MenuNode[], primary: MenuNode[]): NavDropdownCluster[] {
  const reference = new Map<string, { group?: string; rank: number }>();
  let rank = 0;
  for (const parent of primary) {
    if (parent.layout !== "mega3") continue;
    for (const cluster of groupByColumn(parent.children).flat()) {
      for (const item of cluster.items) {
        reference.set(item.href, { group: cluster.heading, rank: rank++ });
      }
    }
  }

  const ordered = children
    .map((child, index) => ({
      item: { label: child.label, href: child.href },
      group: child.group ?? reference.get(child.href)?.group,
      rank: reference.get(child.href)?.rank ?? Infinity,
      index,
    }))
    .sort((a, b) => (a.rank === b.rank ? a.index - b.index : a.rank - b.rank));

  const clusters: NavDropdownCluster[] = [];
  for (const { item, group } of ordered) {
    const last = clusters[clusters.length - 1];
    if (last && last.heading === group) last.items.push(item);
    else clusters.push({ heading: group, items: [item] });
  }
  return clusters;
}

const HIDE_THRESHOLD = 80;

// Shown in the mega menu's left rail, next to the dropdown's own label.
// Only one menu item currently uses the "mega3" layout (Services), so this
// doesn't need to be keyed by item id — if a second mega menu is ever
// added, it'll need its own copy here anyway.
const MEGA_MENU_DESCRIPTION =
  "End-to-end AI product engineering, from architecture to production.";

interface HeaderProps {
  variant?: "light" | "dark";
  primary: MenuTree;
  mobile: MenuTree;
}

export function Header({ variant = "light", primary, mobile }: HeaderProps) {
  const dark = variant === "dark";
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastScrollY = useRef(0);
  const closeMobile = () => setMobileOpen(false);

  const { cta: desktopCta, nav: desktopNav } = splitCta(primary.nodes);
  const { cta: mobileCta, nav: mobileNav } = splitCta(mobile.nodes);

  useEffect(() => {
    lastScrollY.current = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrolledDown = currentScrollY > lastScrollY.current;

      if (currentScrollY <= HIDE_THRESHOLD) {
        setHidden(false);
      } else {
        setHidden(scrolledDown);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // The drawer now overlays the page instead of pushing it down, so lock
  // background scroll while it's open — otherwise the hidden page content
  // behind it would still scroll.
  useEffect(() => {
    if (!mobileOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileOpen]);

  return (
    <header
      className={cn(
        "sticky top-0 z-50 transition-transform duration-300 ease-[cubic-bezier(.22,1,.36,1)] w-full",
        dark ? "bg-neutral-700" : "bg-white",
        hidden && !mobileOpen ? "-translate-y-full" : "translate-y-0",
      )}
    >
      <div className="flex h-(--header-height) w-full items-center justify-between px-6 py-4 md:px-8 lg:px-10 xl:px-16">
        <Logo variant={dark ? "white" : "brand"} className="cursor-pointer" />

        {/* h-(--header-height) -my-4 cancels this row's own py-4 (from the
            parent flex container above) so nav spans the FULL header
            height edge-to-edge, not just the padded content band — see
            NavDropdown's matching h-full trigger wrapper for why: a mega
            menu's hover trigger needs to reach all the way down to the
            header's true bottom edge (where the dropdown panel starts) or
            there's a dead strip of unhovered space the cursor crosses on
            the way down, dropping the menu before it arrives. */}
        <nav className="hidden h-(--header-height) -my-4 items-center gap-12 xl:flex">
          {desktopNav.map((item) =>
            item.children.length > 0 ? (
              <NavDropdown
                key={item.id}
                label={item.label}
                items={item.children}
                columns={item.layout === "mega3" ? groupByColumn(item.children) : undefined}
                description={item.layout === "mega3" ? MEGA_MENU_DESCRIPTION : undefined}
                dark={dark}
              />
            ) : (
              <NavItem key={item.id} href={item.href} dark={dark}>
                {item.label}
              </NavItem>
            ),
          )}
        </nav>

        <div className="hidden xl:block">
          {desktopCta.map((c) => (
            <Button
              key={c.id}
              href={c.href}
              target={c.target}
              rel={c.rel}
              size={"sm"}
              variant={dark ? "secondary" : "primary"}
              className="py-2 px-4"
            >
              {c.label}
            </Button>
          ))}
        </div>

        <Button
          type="button"
          variant="ghost"
          size="auto"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          onClick={() => setMobileOpen((prev) => !prev)}
          className="xl:hidden"
        >
          <span className="relative inline-flex size-6 items-center justify-center">
            <List
              size={24}
              className={cn(
                "absolute transition-all duration-300",
                dark ? "fill-neutral-50" : "fill-neutral-700",
                mobileOpen ? "rotate-45 opacity-0" : "rotate-0 opacity-100",
              )}
            />
            <X
              size={24}
              className={cn(
                "absolute transition-all duration-300",
                dark ? "fill-neutral-50" : "fill-neutral-700",
                mobileOpen ? "rotate-0 opacity-100" : "-rotate-45 opacity-0",
              )}
            />
          </span>
        </Button>
      </div>

      {/* Always mounted so open/close animates instead of popping — a `grid`
          row sized `0fr`/`1fr` cleanly animates to/from an intrinsic height.
          Positioned absolutely (the sticky header above is a positioning
          context) so the drawer overlays the page rather than pushing its
          content down. */}
      <div
        inert={!mobileOpen}
        className={cn(
          "absolute inset-x-0 top-full grid overflow-hidden transition-all duration-300 xl:hidden",
          mobileOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        )}
      >
        <div
          className={cn(
            "flex min-h-0 max-h-[calc(100dvh-var(--header-height))] flex-col gap-6 overflow-y-auto border-t px-6 py-6",
            dark
              ? "border-neutral-600 bg-neutral-700"
              : "border-neutral-200 bg-white",
          )}
        >
          {mobileNav.map((item) => (
            <MobileNavItem
              key={item.id}
              label={item.label}
              href={item.href}
              clusters={
                item.children.length > 0
                  ? clusterMobileChildren(item.children, primary.nodes)
                  : undefined
              }
              onNavigate={closeMobile}
              dark={dark}
            />
          ))}

          {mobileCta.map((c) => (
            <div key={c.id} onClick={closeMobile}>
              <Button
                href={c.href}
                target={c.target}
                rel={c.rel}
                variant={dark ? "secondary" : "primary"}
                className="w-full justify-center"
              >
                {c.label}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </header>
  );
}
